import { useLayoutEffect, useState, type FormEvent } from 'react';
import { useAuthStore } from '@/lib/auth-store';
import { AlertTriangle, Lock, ShieldCheck, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Brandmark } from '@/components/Brandmark';
import { Button } from '@/components/cockpit';

type Mode = 'signin' | 'signup' | 'forgot';

export function LoginPage() {
  // The sign-in screen keeps the console appearance on purpose. The rest of the
  // product moved to the HereToo system; this one is the front door and is
  // staying as it is, by decision rather than by oversight.
  //
  // It has to be pinned explicitly, because the HereToo primitives redefine the
  // shared `.btn-primary` / `.input-field` classes this page uses. Without the
  // pin the gradient Sign In button flattens to a near-black fill on this
  // page's own near-black canvas and loses its edge. Pinning the document theme
  // while the page is mounted keeps every console rule in play, and restores
  // whatever was there on the way out so the app behind it is unaffected.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute('data-theme');
    const previousScheme = root.style.colorScheme;
    root.setAttribute('data-theme', 'console');
    root.style.colorScheme = 'dark';
    return () => {
      if (previous === null) root.removeAttribute('data-theme');
      else root.setAttribute('data-theme', previous);
      root.style.colorScheme = previousScheme;
    };
  }, []);

  const { signIn, signUp, resetPassword, loading, error } = useAuthStore();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [rank, setRank] = useState('');
  const [signupSubmitting, setSignupSubmitting] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [signupResult, setSignupResult] = useState<{ ok: boolean; needsConfirmation?: boolean } | null>(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  const handleSignIn = (e: FormEvent) => {
    e.preventDefault();
    signIn(email, password);
  };

  const handleSignUp = async (e: FormEvent) => {
    e.preventDefault();
    setSignupSubmitting(true);
    setSignupError(null);
    setSignupResult(null);
    const result = await signUp(
      email,
      password,
      fullName.trim() || undefined,
      rank.trim() || undefined,
    );
    setSignupSubmitting(false);
    if (!result.ok) {
      setSignupError(result.error ?? 'Signup failed.');
    } else {
      setSignupResult(result);
    }
  };

  const handleForgot = async (e: FormEvent) => {
    e.preventDefault();
    setResetSubmitting(true);
    setResetError(null);
    const err = await resetPassword(email);
    setResetSubmitting(false);
    if (err) setResetError(err);
    else setResetSent(true);
  };

  return (
    <div className="relative flex min-h-[100dvh] items-center justify-center bg-slate-950 p-4 overflow-hidden">
      {/* Subtle grid background — dispatch console feel */}
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'linear-gradient(rgba(51,65,85,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(51,65,85,0.5) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
        }}
      />

      <div className="relative w-full max-w-md">
        {/* Back to landing — small mono link top-left, lets users
            return to Capt. Moquin's pitch if they hit /login deep. */}
        <a
          href="/"
          className="absolute -top-2 left-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-300 transition-colors"
          title="Back to the pitch"
        >
          <ArrowLeft size={11} />
          Back
        </a>

        {/* Header */}
        <div className="mb-6 flex items-center justify-center gap-3">
          <Brandmark size={48} />
          <div>
            <h1 className="text-2xl tracking-tight text-slate-100 leading-none">
              <span className="font-black">EMS</span><span className="font-normal">PCR</span><span className="wordmark-pulse text-brand-400 font-bold">.</span>
            </h1>
            <p className="mt-1 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              Records Management System
            </p>
          </div>
        </div>

        {mode === 'signin' ? (
          /* ============ SIGN IN ============ */
          <form
            onSubmit={handleSignIn}
            className="rounded-md border border-slate-700 bg-slate-900/80 backdrop-blur-sm shadow-xl"
          >
            <div className="border-b border-slate-700 bg-slate-900 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <Lock size={12} />
              Provider Authentication
            </div>

            <div className="space-y-4 p-5">
              {error && (
                <div className="flex items-start gap-2 rounded-sm border border-red-800 bg-red-950/50 p-3 text-sm text-red-300">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label htmlFor="email" className="data-label mb-1 block">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field font-mono"
                  placeholder="your.name@dept.gov"
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="password" className="data-label">Password</label>
                  <button
                    type="button"
                    onClick={() => {
                      setMode('forgot');
                      setResetError(null);
                      setResetSent(false);
                    }}
                    className="text-[11px] font-semibold uppercase tracking-wider text-sky-300 hover:text-sky-200 underline underline-offset-2 decoration-sky-400/60"
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </div>

              <Button archetype="primary" size="lg" type="submit" disabled={loading} className="w-full">
                {loading ? 'Authenticating…' : 'Sign In'}
              </Button>

              <div className="pt-2 text-center text-[12px] text-slate-400">
                Pilot user? {' '}
                <button
                  type="button"
                  onClick={() => {
                    setMode('signup');
                    setSignupError(null);
                    setSignupResult(null);
                  }}
                  className="font-semibold text-sky-300 hover:text-sky-200 underline underline-offset-2 decoration-sky-400/60"
                >
                  Create your account
                </button>
              </div>
            </div>
          </form>
        ) : mode === 'signup' ? (
          /* ============ PILOT SIGN-UP ============ */
          <form
            onSubmit={handleSignUp}
            className="rounded-md border border-slate-700 bg-slate-900/80 backdrop-blur-sm shadow-xl"
          >
            <div className="border-b border-slate-700 bg-slate-900 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <ShieldCheck size={12} />
              Pilot Account
            </div>

            <div className="space-y-4 p-5">
              {signupResult?.ok ? (
                <div className="flex items-start gap-2 rounded-sm border border-green-800 bg-green-950/50 p-3 text-sm text-green-300">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">Account created</p>
                    <p className="mt-1 text-xs text-green-400/80">
                      {signupResult.needsConfirmation
                        ? <>Check <span className="font-mono">{email}</span> to confirm your email, then come back and sign in.</>
                        : 'You\'re signed in.'}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    EMSPCR.app is in an independent closed pilot. Sign up with
                    your <span className="font-mono">.gov</span> email — name,
                    rank, and email required. You'll get a welcome email with
                    the project overview.
                  </p>

                  {signupError && (
                    <div className="flex items-start gap-2 rounded-sm border border-red-800 bg-red-950/50 p-3 text-sm text-red-300">
                      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                      <span>{signupError}</span>
                    </div>
                  )}

                  <div>
                    <label htmlFor="signup-name" className="data-label mb-1 block">Full name</label>
                    <input
                      id="signup-name"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="input-field"
                      placeholder="John Smith"
                      autoComplete="name"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="signup-rank" className="data-label mb-1 block">Rank or title</label>
                    <input
                      id="signup-rank"
                      type="text"
                      value={rank}
                      onChange={(e) => setRank(e.target.value)}
                      className="input-field"
                      placeholder="Chief, Lieutenant, EMT, Medical Director…"
                      autoComplete="organization-title"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="signup-email" className="data-label mb-1 block">Email (.gov address)</label>
                    <input
                      id="signup-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-field font-mono"
                      placeholder="jsmith@yourtown.gov"
                      required
                      autoComplete="email"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label htmlFor="signup-password" className="data-label mb-1 block">
                      Password — set your own
                    </label>
                    <input
                      id="signup-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-field"
                      placeholder="At least 8 characters"
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>

                  <Button archetype="primary" size="lg" type="submit" disabled={signupSubmitting} className="w-full">
                    {signupSubmitting ? 'Creating account…' : 'Create Account'}
                  </Button>
                </>
              )}

              <button
                type="button"
                onClick={() => {
                  setMode('signin');
                  setSignupError(null);
                  setSignupResult(null);
                }}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
              >
                <ArrowLeft size={12} />
                Back to sign in
              </button>
            </div>
          </form>
        ) : (
          /* ============ FORGOT PASSWORD ============ */
          <form
            onSubmit={handleForgot}
            className="rounded-md border border-slate-700 bg-slate-900/80 backdrop-blur-sm shadow-xl"
          >
            <div className="border-b border-slate-700 bg-slate-900 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <Mail size={12} />
              Password Reset
            </div>

            <div className="space-y-4 p-5">
              {resetSent ? (
                <div className="flex items-start gap-2 rounded-sm border border-green-800 bg-green-950/50 p-3 text-sm text-green-300">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">Reset email sent</p>
                    <p className="mt-1 text-xs text-green-400/80">
                      Check <span className="font-mono">{email}</span> for a link to reset your password.
                      The link expires in 1 hour.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-400">
                    Enter your account email and we'll send you a link to set a new password.
                  </p>

                  {resetError && (
                    <div className="flex items-start gap-2 rounded-sm border border-red-800 bg-red-950/50 p-3 text-sm text-red-300">
                      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                      <span>{resetError}</span>
                    </div>
                  )}

                  <div>
                    <label htmlFor="reset-email" className="data-label mb-1 block">Email</label>
                    <input
                      id="reset-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-field font-mono"
                      placeholder="your.name@dept.gov"
                      required
                      autoComplete="email"
                      autoFocus
                    />
                  </div>

                  <Button archetype="primary" size="lg" type="submit" disabled={resetSubmitting} className="w-full">
                    {resetSubmitting ? 'Sending…' : 'Send Reset Link'}
                  </Button>
                </>
              )}

              <button
                type="button"
                onClick={() => {
                  setMode('signin');
                  setResetError(null);
                  setResetSent(false);
                }}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
              >
                <ArrowLeft size={12} />
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {/* Compliance footer */}
        <div className="mt-4 flex items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-600">
          <ShieldCheck size={10} className="text-green-700" />
          <span>HIPAA · ePHI Encrypted · Audit Logged</span>
        </div>
      </div>
    </div>
  );
}
